<template>
  <v-container>
    <v-form ref="form" v-model="isValid" lazy-validation>
      <v-container>
        <v-row>
          <v-col>
            <v-text-field
              :label="$t('titleOfActivity')"
              required
              v-model="titre"
              maxlength="150"
              :counter="150"
              :rules="basicRules"
            ></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              prepend-icon="mdi-head-cog-outline"
              :label="$t('aimOfActivity')"
              required
              v-model="description"
              :rules="basicRules"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              :label="$t('cognitiveCapacity')"
              required
              filled
              chips
              color="blue-grey lighten-2"
              v-model="fonctionsCognitives"
              :items="fonctions"
              multiple
              item-text="name"
              item-value="name"
              :rules="basicRules"
            ></v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete
              :label="$t('procedures')"
              required
              filled
              chips
              color="blue-grey lighten-2"
              v-model="proceduresUtilisees"
              :items="procedures"
              multiple
              item-text="name"
              item-value="name"
              :rules="basicRules"
            >
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  Il s’agit ici de renseigner la procédure de rééducation auquel
                  votre activité fait référence.
                  <br />
                  Trois choix vous sont proposés :
                  <br />
                  <ul>
                    <li>
                      <b>Amélioration des fonctions déficitaires</b> : Ici il
                      s’agit ici de soutenir, entrainer, répéter certaines
                      cibles cognitives spécifiques en lien avec des
                      modélisations théoriques.
                    </li>
                    <li>
                      <b>Optimisation des fonctions préservées</b> : ici il
                      s’agit d’optimiser les fonctions préservées dans le but
                      d’effectuer une réorganisation fonctionnelle à l’aide de
                      stratégies compensatoires.
                    </li>
                    <li>
                      <b>Aménagement de l’environnement</b> : il s’agira ici de
                      limiter les répercussions dans la vie quotidienne à l’aide
                      d’une prothèse mentale.
                    </li>
                  </ul>
                </v-tooltip>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col style="text-align: center">
            <div>
              <label class="label" for="type-traitement"
                >Type de traitement de l'information</label
              >
            </div>
            <v-btn-toggle
              prepend-icon="mdi-swap-vertical-circle-outline"
              v-model="typeTraitement"
              mandatory
              dense
              id="type-traitement"
              required
            >
              <v-btn value="bottom-up">
                Bottom-up
              </v-btn>
              <v-btn value="top-down">
                Top-down
              </v-btn>
              <v-btn value="bottom-up et top-down">
                Bottom-up et Top-down
              </v-btn>
            </v-btn-toggle>
            <template>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Deux approches principales sont proposées : <br />
                <ul>
                  <li>
                    <b>« Top-down »</b> : Il s’agit d’amener la personne à
                    utiliser les informations sur son propre fonctionnement afin
                    d’influencer sa perception (ex. métacognition).
                  </li>
                  <li>
                    <b>« Bottom-up »</b> : Ces stratégies ont pour but la
                    manipulation et la répétition des informations pour générer
                    des représentations cognitives élaborées et de restaurer les
                    fonctions déficitaires.
                  </li>
                </ul>
              </v-tooltip>
            </template>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              prepend-icon="mdi-progress-wrench"
              label="Matériel(s) utilisé(s)"
              required
              v-model="materiel"
              :rules="basicRules"
            >
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  Veuillez lister le matériel nécessaire à la réalisation de
                  votre activité.
                </v-tooltip>
              </template>
            </v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              prepend-icon="mdi-clock-outline"
              label="Durée de l'activité (minutes)"
              required
              v-model="duree"
              type="number"
              min="0"
              :rules="basicRules && numberRules"
            ></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              prepend-icon="mdi-fast-forward-10"
              label="Nombre de séance(s)"
              required
              v-model="nombreSeances"
              type="number"
              min="0"
              :rules="basicRules && numberRules"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col style="text-align:center">
            <div>
              <label class="label" for="type-intervention">
                <v-icon>mdi-account-multiple-plus-outline</v-icon>
                Type d'intervention</label
              >
            </div>
            <v-btn-toggle
              v-model="typeIntervention"
              mandatory
              dense
              id="type-intervention"
              required
            >
              <v-btn value="individuel">
                Individuel
              </v-btn>
              <v-btn value="groupal">
                Groupal
              </v-btn>
              <v-btn value="individuel et groupal">
                Individuel et Groupal
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <v-row>
          <v-col style="text-align:center">
            <label class="label">Image de couverture</label>
          </v-col>
        </v-row>
        <v-row style="text-align: center">
          <v-col style="text-align:center">
            <v-radio-group
              v-model="defaultCover"
              align="center"
              row
              class="justify-center"
            >
              <v-row>
                <v-col class="pl-0 pr-0">
                  <v-radio
                    label="Attention"
                    :value="defaultsCovers.attention.imageUrl"
                  ></v-radio>
                  <v-img
                    :aspect-ratio="16 / 9"
                    contain
                    width="125"
                    :src="defaultsCovers.attention.fullPath"
                  ></v-img>
                </v-col>
                <v-col class="pl-0 pr-0">
                  <v-radio
                    label="Cognition Sociale"
                    :value="defaultsCovers.cognition.imageUrl"
                  ></v-radio>
                  <v-img
                    :aspect-ratio="16 / 9"
                    contain
                    width="125"
                    :src="defaultsCovers.cognition.fullPath"
                  ></v-img>
                </v-col>
                <v-col class="pl-0 pr-0">
                  <v-radio
                    label="Flexibilité"
                    :value="defaultsCovers.flexibilite.imageUrl"
                  ></v-radio>
                  <v-img
                    :aspect-ratio="16 / 9"
                    contain
                    width="125"
                    :src="defaultsCovers.flexibilite.fullPath"
                  ></v-img>
                </v-col>
                <v-col class="pl-0 pr-0">
                  <v-radio
                    label="Mémoire"
                    :value="defaultsCovers.memoire.imageUrl"
                  ></v-radio>
                  <v-img
                    :aspect-ratio="16 / 9"
                    contain
                    width="125"
                    :src="defaultsCovers.memoire.fullPath"
                  ></v-img>
                </v-col>
                <v-col class="pl-0 pr-0">
                  <v-radio
                    label="Planification"
                    :value="defaultsCovers.planification.imageUrl"
                  ></v-radio>
                  <v-img
                    :aspect-ratio="16 / 9"
                    contain
                    width="125"
                    :src="defaultsCovers.planification.fullPath"
                  ></v-img>
                </v-col>
                <v-col class="pl-0 pr-0">
                  <v-radio label="Autre" value="custom"></v-radio>
                </v-col>
              </v-row>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row v-if="defaultCover === 'custom'">
          <v-col>
            <div>
              <div v-if="!cover" class="coverFrame">
                <span class="helper"></span>
                <span v-if="!cover">
                  <v-icon
                    style="width: calc(100% - 10px);vertical-align: middle"
                    >mdi-plus</v-icon
                  >
                </span>
                <span v-if="cover">
                  <v-progress-circular
                    v-if="cover.inProgress"
                    indeterminate
                    color="primary"
                    style="width: calc(100% - 10px);vertical-align: middle"
                  ></v-progress-circular>
                </span>
              </div>
              <v-img
                class="coverFrame"
                v-if="cover"
                :src="cover.imageUrl"
                contain
                aspect-ratio="1.7"
                style="vertical-align: middle;"
              />
              <div class="coverSubframe">
                <v-btn
                  style="vertical-align: middle;width: 100%"
                  raised
                  :class="cover ? 'error' : 'primary'"
                  id="cover"
                  @click="cover ? onDeleteCover() : onPickCover()"
                  >{{ cover ? "Supprimer" : "Ajouter" }}
                </v-btn>
              </div>
            </div>

            <br />

            <input
              type="file"
              style="display: none"
              :ref="'coverInput'"
              accept=".jpg,.jpeg,.png"
              @change="onCoverPicked($event, 0)"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col style="text-align:center">
            <label class="label"> Images/Vidéos/Documents</label>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="2">
            <div>
              <div class="frame">
                <span class="helper"></span>
                <span v-if="!images[0]">
                  <v-icon
                    style="width: calc(100% - 10px);vertical-align: middle"
                    >mdi-plus</v-icon
                  >
                </span>
                <span v-if="images[0]">
                  <v-progress-circular
                    v-if="images[0].inProgress"
                    indeterminate
                    color="primary"
                    style="width: calc(100% - 10px);vertical-align: middle"
                  ></v-progress-circular>
                </span>
                <img
                  v-if="images[0]"
                  :src="images[0].imageUrl"
                  style="width: calc(100% - 10px);vertical-align: middle"
                />
              </div>
              <div class="subframe">
                <v-btn
                  style="vertical-align: middle;width: 100%"
                  raised
                  :class="images[0] ? 'error' : 'primary'"
                  id="illustration0"
                  @click="images[0] ? onDelete(0) : onPickFile(0)"
                  >{{ images[0] ? "Supprimer" : "Ajouter" }}
                </v-btn>
              </div>
            </div>

            <br />

            <input
              type="file"
              style="display: none"
              :ref="'fileInput0'"
              accept=".pdf,.mp4,.jpg,.jpeg,.png"
              @change="onFilePicked($event, 0)"
            />
          </v-col>
          <v-col cols="2" v-for="(img, index) in images" :key="index">
            <div style="margin-left:30px" v-if="index + 1 < 4">
              <div class="frame">
                <span class="helper"></span>
                <span v-if="!images[index + 1]">
                  <v-icon
                    style="width: calc(100% - 10px);vertical-align: middle"
                    >mdi-plus</v-icon
                  >
                </span>
                <span v-if="images[index + 1]">
                  <v-progress-circular
                    v-if="images[index + 1].inProgress"
                    indeterminate
                    color="primary"
                    style="width: calc(100% - 10px);vertical-align: middle"
                  ></v-progress-circular>
                </span>
                <img
                  v-if="images[index + 1]"
                  :src="images[index + 1].imageUrl"
                  style="width: calc(100% - 10px);vertical-align: middle"
                />
              </div>
              <div class="subframe">
                <v-btn
                  v-if="index + 1 < 5"
                  style="vertical-align: middle;width: 100%"
                  raised
                  :class="images[index + 1] ? 'error' : 'primary'"
                  @click="
                    images[index + 1]
                      ? onDelete(index + 1)
                      : onPickFile(index + 1)
                  "
                  >{{ images[index + 1] ? "Supprimer" : "Ajouter" }}
                </v-btn>
              </div>
              <br />
              <input
                type="file"
                style="display: none"
                :ref="'fileInput' + (index + 1)"
                accept=".pdf,.mp4,.jpg,.jpeg,.png"
                @change="onFilePicked($event, index + 1)"
              />
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              prepend-icon="mdi-cake"
              label="Tranche d'âge"
              required
              filled
              chips
              color="blue-grey lighten-2"
              v-model="trancheAge"
              :items="tranchesAges"
              :rules="basicRules"
            ></v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete
              prepend-icon="mdi-gauge"
              label="Difficulté relative pour le patient"
              required
              filled
              chips
              color="blue-grey lighten-2"
              v-model="difficulte"
              :items="difficultes"
              :rules="basicRules"
            >
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  Le niveau de difficulté est arbitraire et fourni simplement
                  une indication nominale en trois niveaux.
                </v-tooltip>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div style="text-align:right">
              <v-btn class="mr-2" @click="addAndSaveDraft()"
                >{{ $t("saveDraft") }}
              </v-btn>
              <v-btn @click="add()" :disabled="!isFormValid"
                >{{ $t("nextStep") }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "Step1",
  components: {},
  computed: {
    ...mapGetters({
      activity: "activities/getActivity"
    }),
    isFormValid() {
      return this.isValid;
    },
    titre: {
      get: function() {
        return this.activity.titre;
      },
      set: function(newValue) {
        this.activity.titre = newValue;
      }
    },
    description: {
      get: function() {
        return this.activity.description;
      },
      set: function(newValue) {
        this.activity.description = newValue;
      }
    },
    images: {
      get: function() {
        return this.activity.allMedia || [];
      },
      set: function(newValue) {
        this.activity.allMedia = newValue;
      }
    },
    typeTraitement: {
      get: function() {
        return this.activity.typeTraitement;
      },
      set: function(newValue) {
        this.activity.typeTraitement = newValue;
      }
    },
    materiel: {
      get: function() {
        return this.activity.materiel;
      },
      set: function(newValue) {
        this.activity.materiel = newValue;
      }
    },
    duree: {
      get: function() {
        return this.activity.duree;
      },
      set: function(newValue) {
        this.activity.duree = newValue;
      }
    },
    nombreSeances: {
      get: function() {
        return this.activity.nombreSeances;
      },
      set: function(newValue) {
        this.activity.nombreSeances = newValue;
      }
    },
    typeIntervention: {
      get: function() {
        return this.activity.typeIntervention;
      },
      set: function(newValue) {
        this.activity.typeIntervention = newValue;
      }
    },
    fonctionsCognitives: {
      get: function() {
        return this.activity.fonctionsCognitives;
      },
      set: function(newValue) {
        this.activity.fonctionsCognitives = newValue;
      }
    },
    trancheAge: {
      get: function() {
        return this.activity.age;
      },
      set: function(newValue) {
        this.activity.age = newValue;
      }
    },
    cover: {
      get: function() {
        return this.activity.mediaCover;
      },
      set: function(newValue) {
        this.activity.cover = newValue;
        this.activity.mediaCover = newValue;
      }
    },
    defaultCover: {
      get: function() {
        return this.activity.defaultCover;
      },
      set: function(newValue) {
        this.activity.defaultCover = newValue;
      }
    },
    difficulte: {
      get: function() {
        return this.activity.difficulte;
      },
      set: function(newValue) {
        this.activity.difficulte = newValue;
      }
    },
    proceduresUtilisees: {
      get: function() {
        return this.activity.proceduresUtilisees;
      },
      set: function(newValue) {
        this.activity.proceduresUtilisees = newValue;
      }
    }
  },
  props: {
    nextStep: { type: Function },
    saveDraft: { type: Function },
    isDraft: { type: Boolean }
  },
  watch: {
    activity: function() {
      if (!this.isDraft) this.$refs.form.reset();
    },
    cover: function() {
      //let coverTmp = this.cover;
      /*let cover = Object.values(this.defaultsCovers).find(function(cover) {
        if (coverTmp && cover.fullPath === coverTmp.imageUrl) {
          return cover;
        }
      });

      if (cover) {
        this.defaultCover = cover.imageUrl;
      } else {
        this.defaultCover = "custom";
      }*/
    }
  },
  methods: {
    ...mapActions({ validateStep1: "activities/validateStep1" }),
    add() {
      if (this.validate()) {
        this.validateStep1(this.cover ? true : false);
        this.nextStep();
      }
    },
    addAndSaveDraft() {
      this.validateStep1(this.cover ? true : false);
      this.saveDraft();
    },
    onPickCover() {
      this.$refs["coverInput"].click();
    },
    onPickFile(index) {
      if (Array.isArray(this.$refs["fileInput" + index])) {
        this.$refs["fileInput" + index][0].click();
      } else {
        this.$refs["fileInput" + index].click();
      }
    },
    onDelete(index) {
      this.images.splice(index, 1);
    },
    onDeleteCover() {
      this.cover = "";
      this.mediaCover = "";
    },
    async onCoverPicked(event) {
      const files = [...event.target.files];
      let filename = files[0].name;
      if (filename.lastIndexOf(".") <= 0) {
        return alert("Veuillez entrer un fichier valide");
      }
      let fileExtension = filename.substr(
        filename.lastIndexOf(".") + 1,
        filename.length
      );
      if (!this.allowedExtensionCover.includes(fileExtension.toLowerCase())) {
        alert("Extension de fichier non autorisé");
        return false;
      }

      const fileReader = new FileReader();
      fileReader.addEventListener("load", () => {
        this.coverUrl = fileReader.result;
        this.cover.imageUrl = this.coverUrl;
        this.cover.inProgress = false;
      });
      await fileReader.readAsDataURL(files[0]);
      this.cover = {
        imageUrl: this.coverUrl,
        file: files[0],
        inProgress: true
      };
    },
    async onFilePicked(event, index) {
      const files = [...event.target.files];
      let filename = files[0].name;
      if (filename.lastIndexOf(".") <= 0) {
        return alert("Veuillez entrer un fichier valide");
      }
      let fileExtension = filename.substr(
        filename.lastIndexOf(".") + 1,
        filename.length
      );
      if (!this.allowedExtension.includes(fileExtension.toLowerCase())) {
        alert("Extension de fichier non autorisé");
        return false;
      }
      const fileReader = new FileReader();
      fileReader.addEventListener("load", () => {
        this.imageUrl = fileReader.result;
        this.images[index].imageUrl = this.imageUrl;
        this.images[index].inProgress = false;
      });
      await fileReader.readAsDataURL(files[0]);
      this.image = files[0];
      let response = {};
      response.imageUrl = this.imageUrl;
      response.file = this.image;
      this.images.push({
        imageUrl: this.imageUrl,
        file: this.image,
        inProgress: true
      });
    },
    validate() {
      return this.$refs.form.validate();
    }
  },
  data() {
    return {
      allowedExtension: ["png", "jpg", "jpeg", "mp4", "pdf"],
      allowedExtensionCover: ["png", "jpg", "jpeg"],
      difficulteError: false,
      isValid: false,
      stepMax: 0,
      procedures: [
        "Amélioration des fonctions déficitaires",
        "Optimisation des fonctions préservées",
        "Aménagement de l’environnement"
      ],
      tranchesAges: [
        "3 à 6 ans",
        "7 à 10 ans",
        "11 à 13 ans",
        "14 à 17 ans",
        "18 ans et plus",
        "18 à 25 ans",
        "26 à 40 ans",
        "41 à 65 ans",
        "66 à 80 ans",
        "81 ans et plus"
      ],
      difficultes: ["Facile", "Moyenne", "Complexe"],
      fonctions: [
        { name: "Fonctions attentionnelles" },
        { name: "Mémoire à court terme et/ou de travail" },
        { name: "Mémoire à long terme (épisodique, sémantiques, procédurale)" },
        {
          name:
            "Fonctions exécutives (flexibilité, planification, inhibition, mise à jour)"
        },
        { name: "Gnosies" },
        { name: "Capacités visuo-spatiales et constructives" },
        { name: "Comportement(s)" },
        { name: "Vitesse de traitement" },
        { name: "Métacognition" },
        { name: "Cognition sociale" },
        { name: "Vie quotidienne" }
      ],
      imageUrl: "",
      coverUrl: "",
      image: null,
      basicRules: [v => !!v || "Ce champ est requis"],
      numberRules: [v => v > 0 || "Ce champ doit être supérieur à 0"],
      defaultsCovers: {
        planification: {
          fullPath:
            "https://firebasestorage.googleapis.com/v0/b/r-cog-8b6ca.appspot.com/o/Default%2Fplanification.png?alt=media&token=80c7ffd6-8342-43c5-a84c-73b2c0915e8d",
          imageUrl: "Default/planification.png"
        },
        memoire: {
          fullPath:
            "https://firebasestorage.googleapis.com/v0/b/r-cog-8b6ca.appspot.com/o/Default%2Fmemoire.png?alt=media&token=b91657ed-0a06-4ae9-8a2b-7d401cd1713f",
          imageUrl: "Default/memoire.png"
        },
        cognition: {
          fullPath:
            "https://firebasestorage.googleapis.com/v0/b/r-cog-8b6ca.appspot.com/o/Default%2FcognitionSociale.png?alt=media&token=159262d5-a65c-4427-b547-1655feb014f3",
          imageUrl: "Default/cognitionSociale.png"
        },
        attention: {
          fullPath:
            "https://firebasestorage.googleapis.com/v0/b/r-cog-8b6ca.appspot.com/o/Default%2Fattention.png?alt=media&token=22d1e39a-483c-4904-bd92-2d1e6b643f93",
          imageUrl: "Default/attention.png"
        },
        flexibilite: {
          fullPath:
            "https://firebasestorage.googleapis.com/v0/b/r-cog-8b6ca.appspot.com/o/Default%2Fflexibilite.svg?alt=media&token=9552c1ae-d24b-419a-9e95-b8acad75457d",
          imageUrl: "Default/flexibilite.svg"
        }
      }
    };
  }
};
</script>

<style>
.font-size-stars {
  font-size: 26px;
}

.label {
  color: rgba(0, 0, 0, 0.6);
}

.helper {
  display: inline-block;
  height: 100%;
  vertical-align: middle;
}

.frame {
  height: 150px; /* equals max image height */
  width: 160px;
  border: 1px cadetblue solid;
  white-space: nowrap;

  text-align: center;
  margin: 1em 0;
}

.coverFrame {
  height: 300px; /* equals max image height */
  width: 600px;
  border: 1px cadetblue solid;
  white-space: nowrap;
  margin: auto;
  text-align: center;
}

.coverSubframe {
  height: 39px; /* equals max image height */
  width: 600px;
  white-space: nowrap;
  margin: 15px auto;
  text-align: center;
}

.subframe {
  height: 39px; /* equals max image height */
  width: 160px;
  white-space: nowrap;

  text-align: center;
  margin: 1em 0;
}

.v-input--selection-controls .v-input__slot > .v-label,
.v-input--selection-controls .v-radio > .v-label {
  width: 125px;
}
</style>
